import React, { useEffect } from "react";
import PropTypes from 'prop-types';
import BookingAPIService from "../../API/BookingAPI";

import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Button from '@mui/material/Button';
import { IconButton } from '@mui/material';
import { ClientStyles } from "../../Styles/ClientStyles";
import Title from "./../../CommonComponents/Title";
import Subtitle from "../../CommonComponents/SubTitle";
import Heading from "../../CommonComponents/Heading";
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DigitalClock } from '@mui/x-date-pickers/DigitalClock';
import { MultiSectionDigitalClock } from '@mui/x-date-pickers/MultiSectionDigitalClock';
import dayjs, { Dayjs } from 'dayjs';
import { TimeView } from '@mui/x-date-pickers/models';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { CardActionArea } from '@mui/material';
import AddBoxIcon from '@mui/icons-material/AddBox';
import SendIcon from '@mui/icons-material/Send';
import ToggleButton from '@mui/material/ToggleButton';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';


const StyledButton = styled(Button)({
    '&:active': {
      backgroundColor: '#4c6933',
    },
    '&:focus': {
        backgroundColor: '#4c6933',
    },
  });



const shouldDisableTime = (value, view) => {
    const hour = value.hour();
    if (view === 'hours') {
      return hour < 9 || hour > 13;
    }
    if (view === 'minutes') {
      const minute = value.minute();
      return minute > 20 && hour === 13;
    }
    return false;
};

const BookingComponent = props => {

    const [selectedAreaId, setSelectedAreaId] = React.useState('');
    const [partyName, setPartyName] = React.useState('');
    const [partySize, setPartySize] = React.useState('');
    const [selectedDate, setSelectedDate] = React.useState(null);
    const [selectedTime, setSelectedTime] = React.useState(null);
    const [tastingRooms, setTastingRooms] = React.useState([]);
    const [availabilities, setAvailabilities] = React.useState(null);
    const [availabilitiesLoaded, setAvailabilitiesLoaded] = React.useState(false);
    const [colour, setColour] = React.useState('outlined');
    const [selected, setSelected] = React.useState(false);

    const handleAreaChange = (event) => {
        setSelectedAreaId(event.target.value);
    };

    const handlePartySizeChange = (event) => {
        setPartySize(event.target.value);
    };

    const handlePartyNameChange = (event) => {
        setPartyName(event.target.value);
    };

    // Fetch tasting rooms data when component mounts
    useEffect(() => {
        const api = new BookingAPIService();
        api.getTastingRooms("V0ktMDAwMDEjVFItMDAwMDE=").then((response) => {
            setTastingRooms(response.data);
        }).catch(error => {
            console.error('Error fetching tasting rooms:', error);
        });
    }, []);

    // Fetch table availabilities when date, party size, or area ID changes
    useEffect(() => {
        if (selectedDate && partySize && selectedAreaId) {
            const api = new BookingAPIService();
            
            // Format date for API call
            let dateFormatter = new Date(selectedDate);
            let searchDateFormat = dateFormatter.getFullYear();
            
            if(dateFormatter.getMonth() < 10){
                searchDateFormat = searchDateFormat + '-0' + (dateFormatter.getMonth() + 1);
            } else {
                searchDateFormat = searchDateFormat + '-' + (dateFormatter.getMonth() + 1);
            }
            
            if(dateFormatter.getDate() < 10){
                searchDateFormat = searchDateFormat + '-0' + dateFormatter.getDate();
            } else {
                searchDateFormat = searchDateFormat + '-' + dateFormatter.getDate();
            }

            setAvailabilitiesLoaded(false);
            api.getTableAvaliablities("V0ktMDAwMDEjVFItMDAwMDE=", searchDateFormat, partySize, selectedAreaId)
                .then((response) => {
                    setAvailabilities(response.data);
                    setAvailabilitiesLoaded(true);
                })
                .catch(error => {
                    console.error('Error fetching table availabilities:', error);
                    setAvailabilitiesLoaded(true);
                });
        }
    }, [selectedDate, partySize, selectedAreaId]);


    const handleTimeClick = (time) => {
        setSelectedTime(time);
    };

    const handleClockChange = (newTime) => {
        setSelectedTime(newTime);
    };

    // Function to get available times within an hour of selected time
    const getAvailableTimesNearSelectedTime = () => {
        if (!availabilities || !selectedTime) return [];
        
        const selectedHour = selectedTime.hour();
        const selectedMinute = selectedTime.minute();
        const selectedTimeInMinutes = selectedHour * 60 + selectedMinute;
        
        const availableTimes = [];
        
        Object.keys(availabilities.availabilities).forEach(timeSlot => {
            const [hour, minute] = timeSlot.split(':').map(Number);
            const timeInMinutes = hour * 60 + minute;
            
            // Check if time is within 1 hour (60 minutes) of selected time
            if (Math.abs(timeInMinutes - selectedTimeInMinutes) <= 60) {
                availableTimes.push({
                    time: timeSlot,
                    tables: availabilities.availabilities[timeSlot]
                });
            }
        });
        
        return availableTimes.sort((a, b) => {
            const [aHour, aMinute] = a.time.split(':').map(Number);
            const [bHour, bMinute] = b.time.split(':').map(Number);
            return (aHour * 60 + aMinute) - (bHour * 60 + bMinute);
        });
    };

    // Function to format time for display (convert 24h to 12h format)
    const formatTimeForDisplay = (time24) => {
        const [hour, minute] = time24.split(':').map(Number);
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
    };

    let returnJson = {
        "tastingRoomId":"V0ktMDAwMDEjVFItMDAwMDE",
        "tableId":"V0ktMDAwMDEjVFItMDAwMDEjQVItMDIjVEEtMDAx",
        "date":"",
        "time": "",
        "duration": 90,
        "status": "",
        "firstName": "",
        "lastName": "",
        "partySize": 4,
        "email": "foo@bar.com",
        "phone": "18005551212"
    };
    return(
    <>
        <Grid container justifyContent="center">
            <Heading>Book a Tasting</Heading>
        </Grid>
        
        <Grid container spacing={0} >
            <Grid item xs={4}>                   
                <FormControl fullWidth sx={{padding:'1vh', width:'15vw'}}>
                    <InputLabel id="demo-simple-select-label">Table Preference</InputLabel>
                    <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedAreaId}
                    label="Table Preference"
                    onChange={handleAreaChange}
                    >
                    {tastingRooms.map((room) => (
                        <MenuItem key={room.areaId} value={room.areaId}>
                            {room.name}
                        </MenuItem>
                    ))}
                    </Select>
                </FormControl>
                <TextField 
                    id="party-name" 
                    label="Party Name" 
                    variant="outlined" 
                    sx={{padding:'1vh', width:'15vw'}}
                    value={partyName}
                    onChange={handlePartyNameChange}
                />
                <TextField 
                    id="party-size" 
                    label="Party Size" 
                    variant="outlined" 
                    sx={{padding:'1vh', width:'15vw'}}
                    value={partySize}
                    onChange={handlePartySizeChange}
                    type="number"
                />
                <LocalizationProvider dateAdapter={AdapterDayjs} >
                    <DatePicker 
                        label="Date" 
                        sx={{padding:'1vh', width:'15vw'}} 
                        value={selectedDate} 
                        onChange={(newValue)=>{setSelectedDate(newValue)}}
                    />
                </LocalizationProvider>
                    
            </Grid>
            <Grid item xs={2}>
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{padding: '1vh' }}
                    >
                        <LocalizationProvider dateAdapter={AdapterDayjs}  >
                        <MultiSectionDigitalClock
                            skipDisabled
                            views={['hours', 'minutes']}
                            timeSteps={{ hours: 1, minutes: 15 }}
                            shouldDisableTime={shouldDisableTime}
                            ampm={false}
                            sx={{border:'1'}}
                            value={selectedTime}
                            onChange={handleClockChange}
                        />
                    </LocalizationProvider>
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box  sx={{ borderLeft: 1, borderColor:'gray', height:'100%' }} container justifyContent="center">
                    <Grid container justifyContent="center">
                        <Subtitle>Reservations</Subtitle>
                    </Grid>
                    <Grid container spacing={{ xs: 1, md: 1 }}  p={2}>
                        {/* Show reservations only if API has loaded and time is selected */}
                        {availabilitiesLoaded && selectedTime ? (
                            getAvailableTimesNearSelectedTime().length > 0 ? (
                                getAvailableTimesNearSelectedTime().map((timeSlot, index) => (
                                    <Grid item xs={4} key={index}>
                                        <StyledButton 
                                            variant="contained" 
                                            color="primary"  
                                            endIcon={<SendIcon />} 
                                            onClick={() => handleTimeClick(timeSlot.time)}
                                            fullWidth
                                        >
                                            {formatTimeForDisplay(timeSlot.time)}
                                        </StyledButton>
                                    </Grid>
                                ))
                            ) : (
                                <Grid item xs={12}>
                                    <Typography variant="body1" align="center" sx={{ padding: 2 }}>
                                        There are no bookings available at this time
                                    </Typography>
                                </Grid>
                            )
                        ) : availabilitiesLoaded && !selectedTime ? (
                            <Grid item xs={12}>
                                <Typography variant="body1" align="center" sx={{ padding: 2 }}>
                                    Please select a time to see available reservations
                                </Typography>
                            </Grid>
                        ) : !availabilitiesLoaded && selectedDate && partySize && selectedAreaId ? (
                            <Grid item xs={12}>
                                <Typography variant="body1" align="center" sx={{ padding: 2 }}>
                                    Loading available times...
                                </Typography>
                            </Grid>
                        ) : (
                            <Grid item xs={12}>
                                <Typography variant="body1" align="center" sx={{ padding: 2 }}>
                                    Please fill in all booking details to see available times
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Grid>
        </Grid>
    
    </>
    );
}

BookingComponent.defaultProps = {

}
BookingComponent.propTypes = {
    formfilled: PropTypes.bool,
    updateFormFilled:PropTypes.func,

    tableData: PropTypes.object,
    updateTableData:PropTypes.func,

    loaded: PropTypes.bool,
    updateLoaded:PropTypes.func,
}

export default (BookingComponent);
